import { RouteConfig } from 'vue-router';
import app from '@/main';
import { IRouteConfigAuthMeta } from '@/shared';

const auth: IRouteConfigAuthMeta = {
  required: true,
};

const settings = {
  path: '/settings',
  name: 'Account Settings',
  redirect: '/settings/company',
  component: () => import('@/views/settings/Index.vue'),
  meta: {
    auth,
    title: 'Account Settings',
    icon: 'cogs',
    active: false,
  },
};

const accountCustomFields: RouteConfig = {
  path: '/settings/account-custom-fields',
  name: 'SettingsAccountCustomFields',
  component: () => import('@/views/websites/single/global-content/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['custom_fields'],
    },
    title: 'Account Custom Fields',
    icon: 'cogs',
    active: false,
    parent: settings,
    breadcrumbs: (vm) => [
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const superAdminSettings = {
  path: '/superadmin-account-settings',
  name: 'SuperadminAccountSettings',
  redirect: '/superadmin-account-settings/global',
  component: () => import('@/views/superadmin-settings/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Superadmin Account Settings',
    icon: 'cogs',
    active: false,
    parent: settings,
  },
};

const userManagement: RouteConfig = {
  path: '/settings/user-management',
  name: 'SettingsUserManagement',
  redirect: '/settings/user-management/user-groups',
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'User Management',
    icon: 'users',
    active: false,
    noPagePadding: true,
  },
};

const userGroups: RouteConfig = {
  path: '/settings/user-management/user-groups',
  name: 'SettingsUserGroups',
  component: () => import('@/views/settings/user-management/user-groups/IndexV2.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'User Groups',
    icon: 'users',
    active: false,
    parent: userManagement,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/settings/user-management',
        title: 'User Management',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const userGroupsSingle: RouteConfig = {
  path: '/settings/user-management/user-groups/:userGroupId/details',
  name: 'SettingsUserGroupsSingle',
  component: () => import('@/views/settings/user-management/user-groups/single/IndexV2.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'User Group Details',
    active: false,
    useContentBg: true,
    parent: userGroups,
    backButtonTarget: () => ({ target: userGroups, isCustom: false }),
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/settings/user-management',
        title: 'User Management',
      },
      {
        to: '/settings/user-management/user-groups',
        title: 'User Groups',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ],
  },
};

const users: RouteConfig = {
  path: '/settings/user-management/users',
  name: 'SettingsUsers',
  component: () => import('@/views/settings/user-management/users/IndexV2.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'Users',
    active: false,
    parent: userManagement,
    noPagePadding: true,
    icon: 'users',
    breadcrumbs: (vm) => [
      {
        to: '/settings/user-management',
        title: 'User Management',
      },
      {
        to: '/settings/user-management/users',
        title: 'Users',
      },
    ],
  },
};

const usersSingle: RouteConfig = {
  path: '/settings/user-management/users/:userId/details',
  name: 'SettingsUsersSingle',
  component: () => import('@/views/settings/user-management/users/single/IndexV2.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'User Details',
    active: false,
    parent: users,
    backButtonTarget: () => ({ isCustom: false, target: users }),
    noPagePadding: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
      hasCustomText: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/settings/user-management',
        title: 'User Management',
      },
      {
        to: '/settings/user-management/users',
        title: 'Users',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ],
  },
};

const panelTemplates: RouteConfig = {
  path: '/settings/panels',
  name: 'SettingsPanels',
  component: () => import('@/views/settings/panel-templates/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    noPagePadding: true,
    title: 'Panels',
    hasOwnScrollbar: true,
    icon: 'cogs',
    active: false,
    parent: settings,
  },
};

const panelTemplateSingle: RouteConfig = {
  path: '/settings/panels/:id/details',
  name: 'SettingsPanelTemplateSingle',
  component: () => import('@/views/settings/panel-templates/single/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Edit Panel Template',
    hidePageTitle: true,
    hasOwnScrollbar: true,
    noPagePadding: true,
    icon: 'cogs',
    active: false,
    parent: panelTemplates,
  },
};

const company: RouteConfig = {
  path: '/settings/company',
  name: 'SettingsCompany',
  component: () => import('@/views/settings/CompanyProfile.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['company'],
    },
    title: 'Company Profile',
    icon: 'cogs',
    active: false,
    parent: settings,
    breadcrumbs: (vm) => [
      {
        to: '/settings/company',
        title: 'Company Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const contact: RouteConfig = {
  path: '/settings/contact',
  name: 'SettingsContact',
  component: () => import('@/views/settings/ContactDetails.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['company'],
    },
    title: 'Contact Details',
    icon: 'cogs',
    active: false,
    parent: settings,
    noPagePadding: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
      hasLastUpdated: true,
      hasCheckBox: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/settings/company',
        title: 'Company Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const billing: RouteConfig = {
  path: '/settings/billing',
  name: 'SettingsBilling',
  component: () => import('@/views/settings/BillingReports.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['accounting'],
    },
    title: 'Billing',
    icon: 'cogs',
    active: false,
    parent: settings,
    dynamicHideFromNav: () => window.APP_CONFIG.VUE_APP_ENV === 'demo',
  },
};

const invoices: RouteConfig = {
  path: '/settings/invoices',
  name: 'SettingsInvoices',
  component: () => import('@/views/settings/Invoices.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['accounting'],
    },
    title: 'Invoices',
    icon: 'cogs',
    active: false,
    parent: settings,
    infoUrl: 'y',
    dynamicHideFromNav: () => window.APP_CONFIG.VUE_APP_ENV === 'demo',
  },
};

const billingLegacy: RouteConfig = {
  path: '/settings/billing-legacy',
  name: 'SettingsBillingLegacy',
  component: () => import('@/views/settings/BillingReportsLegacy.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Billing (Legacy)',
    icon: 'cogs',
    active: false,
    parent: settings,
    dynamicHideFromNav: () => true,
    breadcrumbs: (vm) => [
      {
        to: '/settings/billingBeta',
        title: 'Accounting',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const accounting: RouteConfig = {
  path: '/settings/accounting',
  name: 'SettingsAccounting',
  component: () => import('@/views/settings/Accounting.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['accounting'],
    },
    title: 'Accounting Overview',
    icon: 'cogs',
    active: false,
    parent: settings,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/settings/billingBeta',
        title: 'Accounting',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const invoicing: RouteConfig = {
  path: '/settings/invoicing',
  name: 'SettingsInvoicing',
  component: () => import('@/views/settings/Invoicing.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Invoicing',
    icon: 'cogs',
    active: false,
    parent: settings,
    dynamicHideFromNav: () => window.APP_CONFIG.VUE_APP_ENV === 'demo',
    breadcrumbs: (vm) => [
      {
        to: '/settings/billingBeta',
        title: 'Accounting',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const superadminAll = {
  path: '/superadmin-account-settings/all-settings',
  name: 'SuperAdminSettingsAll',
  component: () => import('@/views/superadmin-settings/AllSettings.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'All Settings',
    hideFromNav: true,
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
  },
};

const superadminIntegrations: RouteConfig = {
  path: '/superadmin-account-settings/integrations',
  name: 'SuperAdminSettingsIntegrations',
  component: () => import('@/views/superadmin-settings/Integrations.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Integrations',
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/integrations',
        title: 'Integrations',
      },
    ]),
  },
};

const superadminLanguage: RouteConfig = {
  name: 'SuperAdminLanguageSettings',
  path: '/superadmin-account-settings/languages',
  component: () => import('@/views/superadmin-settings/Language.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Language Settings',
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
    dynamicHideFromNav: () => !!app()?.$rsGlobals.$currentClient.parentId,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/languages',
        title: 'Super Admin Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const superadminModules: RouteConfig = {
  path: '/superadmin-account-settings/modules',
  name: 'SuperAdminSettingsModules',
  /**
   * Determine component loaded for this route based on ENV variable.
   * Set as true for testing.
   */
  component: () => import(`@/views/superadmin-settings/ModulesV2.vue`),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Modules',
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/languages',
        title: 'Super Admin Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const superAdminPanelTemplates: RouteConfig = {
  path: '/superadmin-account-settings/panel-templates',
  name: 'SuperAdminSettingsPanelTemplates',
  component: () => import(/* webpackChunkName: "group-settings-panels" */ '@/views/settings/panel-templates/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    noPagePadding: true,
    title: 'Panel Templates',
    hasOwnScrollbar: true,
    icon: 'cogs',
    active: false,
    parent: settings,
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/languages',
        title: 'Super Admin Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const superAdminPanelTemplateSingle: RouteConfig = {
  path: '/superadmin-account-settings/panel-templates/:id/details',
  name: 'SuperAdminSettingsPanelTemplateSingle',
  component: () => import(/* webpackChunkName: "group-settings-panels" */ '@/views/settings/panel-templates/single/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Edit Panel Template',
    hidePageTitle: true,
    hasOwnScrollbar: true,
    noPagePadding: true,
    icon: 'cogs',
    active: false,
    backButtonTarget: () => ({ isCustom: false, target: superAdminPanelTemplates }),
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/languages',
        title: 'Super Admin Settings',
      },
      {
        to: '/superadmin-account-settings/panel-templates',
        title: 'Panel Templates',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ]),
  },
};

const superAdminBuildingStackSettings: RouteConfig = {
  path: '/superadmin-account-settings/bstk-settings',
  name: 'SuperAdminBuildingStackSettings',
  component: () => import('@/views/superadmin-settings/BuildingStackSettings.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Building Stack Settings',
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};

export default [
  settings,
  company,
  contact,
  accountCustomFields,
  userManagement,
  userGroups,
  userGroupsSingle,
  users,
  usersSingle,
  billing,
  invoices,
  billingLegacy,
  accounting,
  invoicing,
  panelTemplates,
  panelTemplateSingle,
  superAdminSettings,
  superadminLanguage,
  superadminAll,
  superadminModules,
  superadminIntegrations,
  superAdminPanelTemplates,
  superAdminPanelTemplateSingle,
  superAdminBuildingStackSettings,
] as Array<RouteConfig>;
